import { Stack, Typography, useTheme } from "@mui/material";
import { CLOUD_FUNCTIONS_BASE_URL } from "@/constants/global";
import { useEffect, useState } from "react";
import Frame from "@/components/Frame";
import { Binary, ClipboardCheck, SearchCheck } from "lucide-react";
import LoadingButton from '@mui/lab/LoadingButton';
import useSnackbar from "@/hooks/useSnackbar";
import { useAppSelector, useResponsive } from "@/hooks";
import { useParams } from "react-router-dom";
import { organizationById } from "@/features/organizations/slice";
import { Organization } from "@/types";
import { useUpdateOrganizationMutation } from "@/features/organizations/api/organizationsApi";
import green from "@mui/material/colors/green";

const verificationTokenUrl = `${CLOUD_FUNCTIONS_BASE_URL}/getVerificationToken`;
const checkVerificationStatus = `${CLOUD_FUNCTIONS_BASE_URL}/verifySearchConsoleSite`;

interface VerificationTokenUrlResponse {
    url: string;
    method: string;
    token: string;
}

const DomainVerification = () => {
    const { palette } = useTheme();
    const [openSnackbar] = useSnackbar();
    const { isExtraSmall } = useResponsive();
    const [isLoading, setIsLoading] = useState(false);
    const [verificationKey, setVerificationKey] = useState<string | null>(null);
    const { organizationId } = useParams<{ organizationId: string }>();
    const organization = useAppSelector(organizationById(organizationId ?? '')) as unknown as Organization;

    const [updateOrganization, updateMutation] = useUpdateOrganizationMutation();

    useEffect(() => {
        if (organization?.googleDomainVerificationToken) {
            setVerificationKey(organization.googleDomainVerificationToken);
        }
    }, [organization?.googleDomainVerificationToken])

    const getSearchConsoleVerificationToken = async () => {
        if (!organization?.domain) {
            openSnackbar('Este comercio aún no cuenta con un dominio definido', 'error');
        }

        setIsLoading(true);

        try {
            const response = await fetch(verificationTokenUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    verificationMethod: "META",
                    url: organization.domain
                })
            });
            const data = await response.json() as VerificationTokenUrlResponse;
            setVerificationKey(data.token); 

            await updateOrganization({
                organizationId: organization.id,
                payload: {
                    googleDomainVerificationToken: data.token
                }
            });

            openSnackbar('Código generado', 'success');
        } catch (error) {
            openSnackbar('Error generando el código de verificación', 'error');
            console.error(error);
            setVerificationKey(null);
        } finally {
            setIsLoading(false);
        }
    }

    const checkDomainVerificationStatus =  async () => {
        if (!organization?.domain) {
            openSnackbar('Este comercio aún no cuenta con un dominio definido', 'error');
        }

        if (verificationKey === null) {
            openSnackbar('Debes generar el código de verificación primero', 'error');
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch(checkVerificationStatus, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    verificationMethod: "META",
                    url: organization.domain
                })
            });
            const data = await response.json() as VerificationTokenUrlResponse;
            console.log("🚀 ~ data:", data);

            await updateOrganization({
                organizationId: organization.id,
                payload: {
                    isDomainVerifiedByGoogle: true
                }
            });

            openSnackbar('Tu sitio fué verificado', 'success');
        } catch (error) {
            openSnackbar('El código no pudo ser verificado', 'error');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Stack spacing={3} direction={isExtraSmall ? 'column' : 'row'} justifyContent="space-between">
            <Frame
                sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex',
                    flexGrow: 1
                }}
            >
                {verificationKey ? (
                    <Stack alignItems="center" gap={3} p={4}>
                        <ClipboardCheck color={green[500]} size={32} />
                        <Stack>
                            <Typography textAlign="center" variant="h3" color={green[500]} fontWeight={400}>
                                Todo listo!
                            </Typography>
                            <Typography textAlign="center" variant="body1">
                                Hemos agregado el código a tu sitio, espera unos minutos antes de probarlo
                            </Typography>
                        </Stack>
                    </Stack>
                ) : (
                    <Stack alignItems="center" gap={3} p={4}>
                        <Binary color={palette.primary.main} size={32} />
                        <Stack>
                            <Typography textAlign="center" variant="h3" color="primary.main" fontWeight={400}>
                                Genera el código de verificación
                            </Typography>
                            <Typography textAlign="center" variant="body1">
                                Al obtenerlo, debes agregarlo el código a tu sitio web
                            </Typography>
                        </Stack>
                        <LoadingButton
                            loading={updateMutation.isLoading || isLoading}
                            onClick={getSearchConsoleVerificationToken}
                        >
                            Generar
                        </LoadingButton>
                    </Stack>
                )}
            </Frame>
            <Frame
                sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex',
                    flexGrow: 1
                }}
            >
                {organization?.isDomainVerifiedByGoogle ? (
                    <Stack alignItems="center" gap={3} p={4}>
                        <ClipboardCheck color={palette.primary.main} size={32} />
                        <Stack>
                            <Typography textAlign="center" variant="h3" color="primary.main" fontWeight={400}>
                                Dominio verificado
                            </Typography>
                            <Typography textAlign="center" variant="body1">
                                El dominio {organization?.domain} ya fué verificado por Google
                            </Typography>
                        </Stack>
                    </Stack>
                ) : (
                    <Stack alignItems="center" gap={3} p={4}>
                        <SearchCheck color={palette.primary.main} size={32} />
                        <Stack>
                            <Typography textAlign="center" variant="h3" color="primary.main" fontWeight={400}>
                                El código fué agregado
                            </Typography>
                            <Typography textAlign="center" variant="body1">
                                Verifica si ya fue detectado, si recibes un error, espera un poco y vuelve a intentarlo
                            </Typography>
                        </Stack>
                        <LoadingButton
                            loading={updateMutation?.isLoading || isLoading}
                            onClick={checkDomainVerificationStatus}
                        >
                            Verificar
                        </LoadingButton>
                    </Stack>
                )}
            </Frame>
        </Stack>
    );
};

export default DomainVerification;
